<template>
    <app-layout title="Pengumuman Pemenang (Executive)">
        <template #header>
            <h2 class="font-semibold text-xl text-gray-800 leading-tight">
                Pengumuman Pemenang
            </h2>
        </template>

        <div class="py-12">
            <div class="max-w-full mx-auto sm:px-6 lg:px-8">
                <div class="bg-white shadow-xl sm:rounded-lg">
                    <div class="overflow-x-auto">
                    <DataTable 
                        ref="dt" 
                        :value="items.data" 
                        :lazy="true" 
                        data-key="id" 
                        :paginator="true" 
                        :rows="10"
                        :loading="loading" 
                        :total-records="items.total" 
                        filter-display="menu"
                        :global-filter-fields="['paket_pengadaan.nama_paket_pengadaan']"
                        v-model:filters="filters"
                        paginator-template="CurrentPageReport FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown"
                        :rows-per-page-options="[5, 10, 25]"
                        current-page-report-template="Menampilkan data ke {first} sampai {last} dari total {totalRecords} data"
                        @page="onPage($event)"
                        class="p-datatable-sm min-w-[900px]">
                        
                        <template #header>
                            <div class="flex justify-content-between align-items-center">
                                <h5 class="m-0">Daftar Pengumuman Pemenang</h5>
                                <span class="p-input-icon-left">
                                    <i class="pi pi-search" />
                                    <InputText 
                                        v-model="filters['global'].value" 
                                        placeholder="Cari nama paket..." 
                                        @input="onGlobalFilter" />
                                </span>
                            </div>
                        </template>

                        <Column field="paket_pengadaan.nama_paket_pengadaan" header="Nama Paket" sortable style="min-width: 200px">
                            <template #body="slotProps">
                                {{ slotProps.data.paket_pengadaan?.nama_paket_pengadaan || '-' }}
                            </template>
                        </Column>

                        <Column field="tahap.nama_tahap" header="Tahap" sortable style="min-width: 150px">
                            <template #body="slotProps">
                                <span class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                    <i class="fas fa-trophy mr-1"></i>
                                    {{ slotProps.data.tahap?.nama_tahap || '-' }}
                                </span>
                            </template>
                        </Column>

                        <Column field="status" header="Status" sortable style="min-width: 120px">
                            <template #body="slotProps">
                                <span
                                    class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium"
                                    :class="slotProps.data.status === 'selesai' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'"
                                >
                                    <i v-if="slotProps.data.status === 'selesai'" class="pi pi-check-circle mr-1"></i>
                                    {{ (slotProps.data.status || '-').toUpperCase() }}
                                </span>
                            </template>
                        </Column>

                        <Column field="nomor_bahp" header="Nomor BAHP" sortable style="min-width: 150px">
                            <template #body="slotProps">
                                {{ slotProps.data.nomor_bahp || '-' }}
                            </template>
                        </Column>

                        <Column field="tanggal_bahp" header="Tanggal BAHP" sortable style="min-width: 120px">
                            <template #body="slotProps">
                                {{ formatDate(slotProps.data.tanggal_bahp) || '-' }}
                            </template>
                        </Column>

                        <Column field="total_hps" header="Total HPS" sortable style="min-width: 150px">
                            <template #body="slotProps">
                                {{ formatCurrency(slotProps.data.total_hps) }}
                            </template>
                        </Column>

                        <Column field="pemenang_info" header="Pemenang" style="min-width: 250px">
                            <template #body="slotProps">
                                <div v-if="getPemenang(slotProps.data)">
                                    <div class="flex items-center">
                                        <i class="fas fa-trophy text-yellow-500 mr-2"></i>
                                        <div>
                                            <div class="text-sm font-medium text-green-600">
                                                {{ getPemenang(slotProps.data).penyedia?.nama_perusahaan }}
                                            </div>
                                            <div class="text-xs text-gray-500">
                                                Nilai: {{ formatCurrency(getTotalPenawaran(getPemenang(slotProps.data))) }}
                                            </div>
                                            <div class="text-xs text-gray-500" v-if="getPemenang(slotProps.data).tanggal_penetapan_pemenang">
                                                Tanggal: {{ formatDate(getPemenang(slotProps.data).tanggal_penetapan_pemenang) }}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <span v-else class="text-gray-400">Belum ada pemenang</span>
                            </template>
                        </Column>

                        <Column header="Aksi" style="min-width: 200px">
                            <template #body="slotProps">
                                <div class="flex gap-2">
                                    <Button
                                        icon="pi pi-eye"
                                        label="Lihat Detail"
                                        severity="info"
                                        size="small"
                                        @click="viewDetail(slotProps.data)"
                                        text />
                                </div>
                            </template>
                        </Column>

                        <template #empty>
                            <div class="text-center py-8 text-gray-500">
                                <i class="fas fa-inbox text-3xl mb-3 text-gray-300"></i>
                                <p>Tidak ada data pengumuman pemenang</p>
                            </div>
                        </template>
                    </DataTable>
                    </div>
                </div>
            </div>
        </div>

        <Toast position="top-right" />
    </app-layout>
    </template>

<script>
import { defineComponent } from 'vue'
import AppLayout from '@/Layouts/AppLayout.vue'
import { FilterMatchMode } from 'primevue/api'
import DataTable from 'primevue/datatable'
import Column from 'primevue/column'
import InputText from 'primevue/inputtext'
import Button from 'primevue/button'
import Toast from 'primevue/toast'
import { useToast } from 'primevue/usetoast'

export default defineComponent({
    components: {
        AppLayout,
        DataTable,
        Column,
        InputText,
        Button,
        Toast,
    },

    setup() {
        const toast = useToast()
        return { toast }
    },

    data() {
        return {
            items: {
                data: [],
                total: 0
            },
            loading: false,
            filters: {
                global: { value: null, matchMode: FilterMatchMode.CONTAINS }
            },
            searchTimeout: null,
        }
    },

    mounted() {
        this.loadData()
    },

    methods: {
        async loadData(page = 1, rows = 10, globalFilter = '') {
            try {
                this.loading = true
                const params = { page, per_page: rows, search: globalFilter }
                const response = await axios.get(route('dokumen_pengadaan.list_pengumuman_pemenang_exec.data'), { params })
                if (response.data.success && response.data.payload) {
                    this.items = response.data.payload
                } else {
                    this.items = { data: [], total: 0 }
                }
            } catch (error) {
                this.toast.add({ severity: 'error', summary: 'Error', detail: 'Gagal memuat data', life: 3000 })
            } finally {
                this.loading = false
            }
        },

        onPage(event) {
            const page = event.page + 1
            const rows = event.rows
            const globalFilter = this.filters.global.value || ''
            this.loadData(page, rows, globalFilter)
        },

        onGlobalFilter() {
            clearTimeout(this.searchTimeout)
            this.searchTimeout = setTimeout(() => {
                const globalFilter = this.filters.global.value || ''
                this.loadData(1, 10, globalFilter)
            }, 300)
        },

        viewDetail(dokumen) {
            this.$inertia.visit(route('dokumen_pengadaan.list_pengumuman_pemenang_exec.detail', dokumen.id))
        },

        getPemenang(dok) {
            return (dok.kepersertaan_pengadaan || []).find(p => p.is_pemenang)
        },
        getTotalPenawaran(pengajuan) {
            if (!pengajuan?.detail_penawaran_kepersertaan_pengadaan) return 0
            return pengajuan.detail_penawaran_kepersertaan_pengadaan.reduce((sum, item) => sum + (item.volume * item.harga_satuan), 0)
        },
        formatCurrency(value) {
            if (value === null || value === undefined) return 'Rp 0'
            return new Intl.NumberFormat('id-ID', { style: 'currency', currency: 'IDR', minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(value)
        },
        formatDate(dateString) {
            if (!dateString) return null
            return new Date(dateString).toLocaleDateString('id-ID', { day: '2-digit', month: 'long', year: 'numeric' })
        },
    }
})
</script>

<style scoped>
.p-datatable-sm .p-datatable-tbody > tr > td {
    padding: 0.5rem;
}
</style>

